import { ErrorBoundary } from '@/components';
import { Box, Flex, FlexProps, Heading } from '@blockanalitica/ui';
import * as Sentry from '@sentry/react';
import { ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';

export interface PageProps extends Omit<FlexProps, 'title'> {
  title?: string | ReactNode;
  actions?: ReactNode;
  headerFlexDirection?: FlexProps['flexDirection'];
  children: ReactNode;
}

const PageWrapper = styled(Box)<PageProps>`
  position: relative;
  padding: ${(props) => props.theme.space.m};

  &:after {
    content: '';
    position: absolute;
    z-index: -500;
    inset: 0;
    background: rgba(12, 12, 13, 0.65);
    mix-blend-mode: multiply;

    ${(props) => css`
      border: solid 1px ${props.theme.colors.neutral};
      border-radius: ${props.theme.sizes[3]};
    `}
  }
`;

const PageContainer = styled(Flex)<FlexProps>`
  &:before {
    content: '';
    border-radius: ${(props) => props.theme.sizes['3']};
    position: absolute;
    z-index: -500;
    inset: 0;
    backdrop-filter: blur(50px);
    -webkit-backdrop-filter: blur(50px);
  }
`;

export default function Page({
  title,
  actions,
  headerFlexDirection = 'row',
  children
}: PageProps) {
  const { pathname } = useLocation();

  const crumbs = useMemo(() => {
    const pathArray = pathname.split('/').filter((v) => v.length > 0);

    const crumbsList = pathArray.map((subPath, index) => {
      const href = '/' + pathArray.slice(0, index + 1).join('/');
      const text = subPath[0].toUpperCase() + subPath.slice(1);

      return { href, text };
    });

    return crumbsList;
  }, [pathname]);

  return (
    <Sentry.ErrorBoundary fallback={<ErrorBoundary />}>
      <PageWrapper>
        <PageContainer flexDirection="column" gap="m">
          {title || actions || crumbs.length > 0 ? (
            <Flex flexDirection="column" gap="s">
              {crumbs.length > 0 ? <Breadcrumbs crumbs={crumbs} /> : null}
              <Flex
                gap="s"
                justifyContent="space-between"
                alignItems="center"
                flexDirection={headerFlexDirection}>
                <Flex gap="3xs" alignSelf="flex-start">
                  {typeof title === 'string' ? (
                    <Heading size="1">{title}</Heading>
                  ) : (
                    title
                  )}
                </Flex>
                {actions ? (
                  <Flex
                    gap="xs-s"
                    flexWrap={['wrap', 'nowrap']}
                    justifyContent="flex-end"
                    alignItems="center"
                    flexBasis={['min-content', 'content']}>
                    {actions}
                  </Flex>
                ) : null}
              </Flex>
            </Flex>
          ) : null}

          {children}
        </PageContainer>
      </PageWrapper>
    </Sentry.ErrorBoundary>
  );
}
